// src/components/create-project/context/ProjectContext.tsx
import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';
import { Project, ProjectFormData } from '../types/project';
import { getProjects, createProject, deleteProject, getProjectById } from '../services/projectService';
import { useAuth } from '@/contexts/AuthContext';

interface ProjectContextType {
  projects: Project[];
  selectedProject: Project | null;
  loading: boolean;
  error: string | null;
  fetchProjects: () => Promise<void>;
  addProject: (data: ProjectFormData) => Promise<void>;
  removeProject: (id: string) => Promise<void>;
  selectProject: (project: Project) => Promise<void>;
  fetchProjectById: (id: string) => Promise<void>;
}

const SELECTED_PROJECT_KEY = {
  ID: 'selectedProjectId',
  NAME: 'selectedProjectName',
  USE_CASES: 'selectedProjectUseCases'
};

export const ProjectContext = createContext<ProjectContextType | null>(null);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { refreshStaticData } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(() => {
    // Initialize from session storage
    const storedId = sessionStorage.getItem(SELECTED_PROJECT_KEY.ID);
    const storedName = sessionStorage.getItem(SELECTED_PROJECT_KEY.NAME);
    const storedUseCases = sessionStorage.getItem(SELECTED_PROJECT_KEY.USE_CASES);
    return storedId && storedName ? {
      projectId: storedId,
      name: storedName,
      useCases: storedUseCases ? JSON.parse(storedUseCases) : []
    } as Project : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasAutoSelected = useRef(false);

  const addProject = async (projectData: ProjectFormData) => {
    setLoading(true);
    try {
      const response = await createProject(projectData);
      if (response.success) {
        setProjects(prev => [response.data, ...prev]);
      }
    } catch (err) {
      setError('Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  // Clear selected project from session storage
  const clearSelectedProject = () => {
    setSelectedProject(null);
    sessionStorage.removeItem(SELECTED_PROJECT_KEY.ID);
    sessionStorage.removeItem(SELECTED_PROJECT_KEY.NAME);
    sessionStorage.removeItem(SELECTED_PROJECT_KEY.USE_CASES);
  };

  // Remove project
  const removeProject = async (id: string) => {
    try {
      const response = await deleteProject(id);
      if (response.success) {
        setProjects(prev => prev.filter(project => project.projectId !== id));
        if (selectedProject?.projectId === id) {
          clearSelectedProject();
          window.location.reload();
        }
      }
    } catch (err) {
      setError('Failed to delete project');
    }
  };

  // Select project and store in session storage
  // Refresh static data to get project-scoped permissions
  const selectProject = useCallback(async (project: Project) => {
    setSelectedProject(project);
    sessionStorage.setItem(SELECTED_PROJECT_KEY.ID, project.projectId);
    sessionStorage.setItem(SELECTED_PROJECT_KEY.NAME, project.name);
    sessionStorage.setItem(SELECTED_PROJECT_KEY.USE_CASES, JSON.stringify(project.useCases || []));
    setProjects(prevProjects => [...prevProjects]);
    
    // Refresh static data to get permissions scoped to the newly selected project
    await refreshStaticData();
  }, [refreshStaticData]);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getProjects();
      if (response.success) {
        const apiProjects = response.data.projects || [];
        setProjects(apiProjects);

        // Check if stored project is in the list of accessible projects
        const storedProjectId = sessionStorage.getItem(SELECTED_PROJECT_KEY.ID);
        const storedProjectIsAccessible = storedProjectId && 
          apiProjects.some((p: Project) => p.projectId === storedProjectId);

        if (!hasAutoSelected.current) {
          hasAutoSelected.current = true;
          
          if (storedProjectIsAccessible) {
            // Stored project is accessible - update selectedProject with full data from API
            const fullProject = apiProjects.find((p: Project) => p.projectId === storedProjectId);
            if (fullProject) {
              setSelectedProject(fullProject);
              // Refresh static data to ensure permissions are scoped to this project
              await refreshStaticData();
            }
          } else if (apiProjects.length > 0) {
            // Stored project is not accessible or doesn't exist - select first accessible project
            if (storedProjectId) {
              // Clear invalid stored project
              clearSelectedProject();
            }
            const projectToSelect = apiProjects[0];
            await selectProject(projectToSelect);
          } else {
            // No accessible projects - clear any stored selection
            clearSelectedProject();
          }
        }
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, [selectProject, refreshStaticData]);

  // On provider mount, fetch projects and ensure a project is selected
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);
  // Fetch project by id
  const fetchProjectById = async (id: string) => {
    try {
      const response = await getProjectById(id);
      if (response.success) {
        setSelectedProject(response.data);
      }else{
        clearSelectedProject();
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      clearSelectedProject();
    }
  };

  return (
    <ProjectContext.Provider 
      value={{
        projects,
        selectedProject,
        loading,
        error,
        fetchProjects,
        addProject,
        removeProject,
        selectProject,
        fetchProjectById,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};
